import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  darken,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import EngineeringIcon from "@mui/icons-material/Engineering";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PaymentsIcon from "@mui/icons-material/Payments";
import BoltIcon from "@mui/icons-material/Bolt";
import { useNavigate } from "react-router-dom";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { appTheme } from "../../../shared/theme";
import { CustomRoundButton } from "Layout/CustomButton";
import { scroller } from "react-scroll";



const StoreBadges = ({ center }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.25,
      justifyContent: center ? "center" : "flex-start",
      alignItems: "center",
      flexWrap: "wrap",
    }}
  >
    <Box
      component="a"
      href="https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        lineHeight: 0,
        display: "inline-flex",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "transparent",
      }}
    >
      <Box
        component="img"
        alt="Download on the App Store"
        src="/thirdParty/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>

    <Box
      component="a"
      href="https://play.google.com/store/apps/details?id=com.sevenday.mobile&pcampaignid=web_share7DAY"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Get it on Google Play"
        src="/thirdParty/GetItOnGooglePlay_Badge_Web_color_English.png"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>
  </Box>
);

export const FeaturesLandingPage = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    comment: "",
  });

  const validationCheck = useMemo(
    () => Object.values(values).some((v) => !v?.trim()),
    [values],
  );

  const headingFont =
    appTheme.typography?.h1?.fontFamily ||
    appTheme.typography?.body1?.fontFamily;
  const bodyFont = appTheme.typography?.body1?.fontFamily;

  const handleContact = () => {
    navigate("/");
    setTimeout(() => {
      scroller.scrollTo("contact", {
        smooth: true,
        offset: 100,
        duration: 2000,
        delay: 0,
      });
    }, 0);
  };

  const ContactTile = ({ icon, label, title, onClick, href }) => {
    const content = (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 3,
          border: `1px solid ${darken(appTheme.palette.white.main, 0.08)}`,
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(6px)",
          transition: "transform 160ms ease, background 160ms ease",
          cursor: onClick || href ? "pointer" : "default",
          "&:hover": {
            transform: "translateY(-2px)",
            background: "rgba(255,255,255,0.09)",
          },
        }}
        onClick={onClick}
      >
        <CardContent
          sx={{
            p: { xs: 2, md: 2.5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1,
          }}
        >
          <CustomRoundButton aria-label={label}>{icon}</CustomRoundButton>

          <Typography
            sx={{
              color: appTheme.palette.yellow.main,
              fontSize: { xs: 16, md: 18, lg: 20 },
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontFamily: headingFont,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              color: appTheme.palette.white.main,
              fontSize: { xs: 13, md: 14 },
              fontFamily: bodyFont,
              opacity: 0.9,
            }}
          >
            {title}
          </Typography>
        </CardContent>
      </Card>
    );

    if (href) {
      return (
        <Box
          component="a"
          href={href}
          sx={{ textDecoration: "none", display: "block", height: "100%" }}
        >
          {content}
        </Box>
      );
    }

    return content;
  };

  const FeaturePill = ({ icon, title, body }) => (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: { xs: 1.5, md: 2 },
        borderRadius: 3,
        border: `1px solid ${darken(appTheme.palette.white.main, 0.08)}`,
        background:
          "linear-gradient(180deg, rgba(92,181,179,0.10) 0%, rgba(255,255,255,1) 60%)",
        boxShadow: "0 14px 40px rgba(20,55,74,0.06)",
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(92,181,179,0.14)",
          color: appTheme.palette.blue.main,
          flex: "0 0 auto",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: headingFont,
            color: appTheme.palette.blue.main,
            fontWeight: 900,
            letterSpacing: ".04rem",
            textTransform: "uppercase",
            fontSize: { xs: 13, md: 14 },
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 0.6,
            fontFamily: bodyFont,
            color: appTheme.palette.gray.main,
            lineHeight: 1.75,
            fontSize: { xs: 13.5, md: 14.5 },
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );

  const StepCard = ({ n, title, body, icon }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 4,
        border: `1px solid ${darken(appTheme.palette.white.main, 0.1)}`,
        background:
          "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(246,248,249,1) 100%)",
        boxShadow: "0 18px 55px rgba(20,55,74,0.07)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2.3, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.4,
          }}
        >
          <Chip
            label={`STEP ${n}`}
            sx={{
              fontFamily: bodyFont,
              fontWeight: 900,
              letterSpacing: ".12rem",
              borderRadius: 999,
              backgroundColor: "rgba(244,180,79,0.20)",
              color: appTheme.palette.blue.main,
            }}
          />
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(20,55,74,0.06)",
              color: appTheme.palette.blue.main,
            }}
          >
            {icon}
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: headingFont,
            color: appTheme.palette.blue.main,
            fontWeight: 950,
            letterSpacing: ".04rem",
            textTransform: "uppercase",
            fontSize: { xs: 16, md: 17 },
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 1.1,
            fontFamily: bodyFont,
            color: appTheme.palette.gray.main,
            lineHeight: 1.85,
            fontSize: { xs: 13.5, md: 14.5 },
          }}
        >
          {body}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        id="product"
        sx={{
          background: appTheme.palette.blue.main,
          color: appTheme.palette.white.main,
          pt: { xs: 20, md: 30, lg: 20 },
          pb: { xs: 8, md: 9 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(900px 400px at 20% 20%, rgba(255,255,255,0.12), transparent 60%), radial-gradient(700px 380px at 85% 35%, rgba(92,181,179,0.18), transparent 60%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Stack
            spacing={1}
            sx={{ alignItems: { xs: "flex-start", md: "center" } }}
          >
            <Typography
              variant={isSmDown ? "h4" : "h2"}
              sx={{
                fontFamily: headingFont,
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: "0.05em",
                lineHeight: 1.05,
              }}
            >
              Features
            </Typography>

            <Typography
              sx={{
                fontFamily: bodyFont,
                opacity: 0.85,
                maxWidth: 760,
                textAlign: { xs: "left", md: "center" },
                fontSize: { xs: 14, md: 16 },
                color: appTheme.palette.yellow.main,
              }}
            >
              Explore the features that make bookings effortless.
            </Typography>
          </Stack>

          <Grid container spacing={2} sx={{ mt: { xs: 4, md: 5 } }}>
            <Grid item xs={12} md={4}>
              <ContactTile
                label="For Hosts"
                title="Create. Get booked. Stay organised."
                onClick={() => navigate("/product/hosts")}
                icon={
                  <RoomServiceIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactTile
                label="For Guests"
                title="Work bookings. Personal perks. Same easy experience."
                onClick={() => navigate("/product/guests")}
                icon={
                  <WorkOutlineIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactTile
                label="For Contractors"
                title="List once. Get discovered by hosts and booked by guests."
                onClick={() => navigate("/product/contractors")}
                icon={
                  <EngineeringIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          position: "relative",
          backgroundColor: appTheme.palette.white.main,
          overflow: "hidden",
          py: { xs: 7, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(900px 520px at 12% 0%, rgba(92,181,179,0.14) 0%, rgba(255,255,255,0) 60%), radial-gradient(900px 520px at 92% 12%, rgba(244,180,79,0.14) 0%, rgba(255,255,255,0) 58%)",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ pr: { md: 2 } }}>
                <Typography
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 950,
                    letterSpacing: ".04rem",
                    textTransform: "uppercase",
                    color: appTheme.palette.blue.main,
                    fontSize: { xs: 24, sm: 30, md: 34 },
                    lineHeight: 1.15,
                  }}
                >
                  Everything you need,
                  <br />
                  without the chaos.
                </Typography>

                <Typography
                  sx={{
                    mt: 1.6,
                    fontFamily: bodyFont,
                    color: appTheme.palette.gray.main,
                    lineHeight: 1.9,
                    fontSize: { xs: 14.5, md: 16 },
                    maxWidth: 520,
                  }}
                >
                  Plan the booking, invite the crew, coordinate access, and keep
                  payments tidy. Hosts, guests, and contractors all get a clean
                  workflow in one place.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2.2, flexWrap: "wrap", rowGap: 1 }}
                >
                  <Chip
                    icon={<BoltIcon />}
                    label="Faster coordination"
                    sx={{
                      fontFamily: bodyFont,
                      fontWeight: 800,
                      borderRadius: 999,
                      backgroundColor: "rgba(92,181,179,0.14)",
                      color: appTheme.palette.blue.main,
                    }}
                  />
                  <Chip
                    icon={<VerifiedUserIcon />}
                    label="Smoother check-ins"
                    sx={{
                      fontFamily: bodyFont,
                      fontWeight: 800,
                      borderRadius: 999,
                      backgroundColor: "rgba(244,180,79,0.18)",
                      color: appTheme.palette.blue.main,
                    }}
                  />
                  <Chip
                    icon={<PaymentsIcon />}
                    label="Cleaner billing"
                    sx={{
                      fontFamily: bodyFont,
                      fontWeight: 800,
                      borderRadius: 999,
                      backgroundColor: "rgba(20,55,74,0.06)",
                      color: appTheme.palette.blue.main,
                    }}
                  />
                </Stack>

                <Box sx={{ mt: 3 }}>
                  <Button
                    onClick={handleContact}
                    sx={{
                      px: 2.6,
                      py: 1.2,
                      borderRadius: 3,
                      backgroundColor: appTheme.palette.blue.main,
                      color: appTheme.palette.white.main,
                      fontWeight: 950,
                      letterSpacing: ".10rem",
                      textTransform: "uppercase",
                      "&:hover": {
                        opacity: 0.92,
                        backgroundColor: appTheme.palette.blue.main,
                      },
                    }}
                  >
                    Get In Touch
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={6}>
                <Grid item xs={12} sm={6}>
                  <FeaturePill
                    icon={<CalendarMonthIcon />}
                    title="Calendar-first planning"
                    body="See availability, align dates, and keep everyone synced without spreadsheet pain."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FeaturePill
                    icon={<GroupsIcon />}
                    title="Invite the team"
                    body="Add attendees so they can view the booking, get reminders, and stay in the loop."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FeaturePill
                    icon={<VerifiedUserIcon />}
                    title="Access made simple"
                    body="Smoother arrivals with modern access options, less key-hunting, more vibes."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FeaturePill
                    icon={<PaymentsIcon />}
                    title="Billing that behaves"
                    body="Reservation charges are transparent and tidy for bookings, add-ons, and services."
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 5, md: 7 } }}>
            <Grid container spacing={2.2} alignItems="stretch">
              <Grid item xs={12} md={4}>
                <StepCard
                  n={1}
                  title="Pick the experience"
                  body="Spaces, venues, stays, and add-ons—choose what fits the team (or the weekend)."
                  icon={<BoltIcon />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StepCard
                  n={2}
                  title="Invite + coordinate"
                  body="Share details with attendees and keep updates in one place so nobody misses the plan."
                  icon={<GroupsIcon />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StepCard
                  n={3}
                  title="Arrive like a pro"
                  body="Clear access, cleaner check-in, and an easy handover so the day starts smooth."
                  icon={<VerifiedUserIcon />}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              borderRadius: 5,
              overflow: "hidden",
              border: `1px solid ${darken(appTheme.palette.white.main, 0.12)}`,
              boxShadow: "0 20px 70px rgba(20,55,74,0.08)",
              background: {
                xs: "linear-gradient(180deg, rgba(20,55,74,1) 0%, rgba(20,55,74,1) 52%, rgba(255,255,255,1) 52%, rgba(255,255,255,1) 100%)",
                md: "linear-gradient(90deg, rgba(20,55,74,1) 0%, rgba(20,55,74,1) 56%, rgba(92,181,179,0.16) 56%, rgba(244,180,79,0.12) 100%)",
              },
            }}
          >
            <Grid container>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    p: { xs: 2.6, md: 4 },
                    color: appTheme.palette.white.main,
                    // textAlign: { xs: "center", md: "left" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", md: "flex-start" },
                      gap: 1,
                      mb: 1.2,
                      opacity: 0.95,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,255,255,0.10)",
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                    >
                      <RocketLaunchIcon
                        sx={{
                          color: appTheme.palette.yellow.main,
                          fontSize: 20,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: bodyFont,
                        fontWeight: 900,
                        letterSpacing: ".14rem",
                        textTransform: "uppercase",
                        fontSize: 12,
                        color: "rgba(255,255,255,0.9)",
                      }}
                    >
                      Live demo vibes
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: appTheme.palette.yellow.main,
                      fontFamily: headingFont,
                      fontWeight: 950,
                      letterSpacing: ".05rem",
                      textTransform: "uppercase",
                      fontSize: { xs: 18, md: 22 },
                      lineHeight: 1.2,
                    }}
                  >
                    Ready to see it in action?
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.1,
                      fontFamily: bodyFont,
                      opacity: 0.9,
                      lineHeight: 1.85,
                      fontSize: { xs: 13.5, md: 15 },
                      maxWidth: 560,
                      mx: { xs: "auto", md: 0 },
                    }}
                  >
                    Jump into the mobile app and explore how hosts, guests, and
                    contractors move through the same clean flow.
                  </Typography>

                  <Box
                    sx={{
                      mt: 2.1,
                      display: "flex",
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    <StoreBadges center={isSmDown} />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ p: 2 }}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: {
                      xs: "linear-gradient(180deg, rgba(246,248,249,1) 0%, rgba(255,255,255,1) 100%)",
                      md: "transparent",
                    },
                  }}
                >
                  <Stack
                    spacing={1}
                    sx={{
                      width: "100%",
                      maxWidth: 460,
                      p: { xs: 2, md: 2.2 },
                      borderRadius: 4,
                      background: {
                        xs: "rgba(255,255,255,1)",
                        md: "rgba(255,255,255,0.92)",
                      },
                      border: `1px solid ${darken(appTheme.palette.white.main, 0.12)}`,
                      boxShadow: {
                        xs: "0 14px 40px rgba(20,55,74,0.08)",
                        md: "none",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: headingFont,
                          color: appTheme.palette.blue.main,
                          fontWeight: 950,
                          letterSpacing: ".04rem",
                          textTransform: "uppercase",
                          fontSize: 14,
                        }}
                      >
                        Quick paths
                      </Typography>

                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(20,55,74,0.06)",
                          color: appTheme.palette.blue.main,
                        }}
                      >
                        <PhoneIphoneIcon sx={{ fontSize: 18 }} />
                      </Box>
                    </Box>

                    <Button
                      onClick={() => navigate("/product/hosts")}
                      sx={{
                        justifyContent: "space-between",
                        borderRadius: 3,
                        px: 2,
                        py: 1.15,
                        backgroundColor: "rgba(92,181,179,0.12)",
                        color: appTheme.palette.blue.main,
                        fontWeight: 900,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "rgba(92,181,179,0.18)" },
                      }}
                    >
                      For Hosts
                      <RoomServiceIcon />
                    </Button>

                    <Button
                      onClick={() => navigate("/product/guests")}
                      sx={{
                        justifyContent: "space-between",
                        borderRadius: 3,
                        px: 2,
                        py: 1.15,
                        backgroundColor: "rgba(244,180,79,0.16)",
                        color: appTheme.palette.blue.main,
                        fontWeight: 900,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "rgba(244,180,79,0.22)" },
                      }}
                    >
                      For Guests
                      <WorkOutlineIcon />
                    </Button>

                    <Button
                      onClick={() => navigate("/product/contractors")}
                      sx={{
                        justifyContent: "space-between",
                        borderRadius: 3,
                        px: 2,
                        py: 1.15,
                        backgroundColor: "rgba(20,55,74,0.06)",
                        color: appTheme.palette.blue.main,
                        fontWeight: 900,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "rgba(20,55,74,0.10)" },
                      }}
                    >
                      For Contractors
                      <EngineeringIcon />
                    </Button>

                    <Button
                      onClick={handleContact}
                      sx={{
                        mt: 0.4,
                        justifyContent: "center",
                        borderRadius: 3,
                        px: 2,
                        py: 1.15,
                        backgroundColor: appTheme.palette.blue.main,
                        color: appTheme.palette.white.main,
                        fontWeight: 950,
                        letterSpacing: ".08rem",
                        textTransform: "uppercase",
                        "&:hover": {
                          opacity: 0.92,
                          backgroundColor: appTheme.palette.blue.main,
                        },
                      }}
                    >
                      Talk to us
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
